import { useEffect, useState } from "react";
import { MailWarning, RotateCcw, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export function AdminEmailHealth() {
  const [rows, setRows] = useState(null);
  const [resetting, setResetting] = useState("");

  const load = () =>
    api.get("/admin/email-suppressed").then((r) => setRows(r.data.suppressed || [])).catch(() => setRows([]));

  useEffect(() => { load(); }, []);

  const reset = async (email) => {
    setResetting(email);
    try {
      const { data } = await api.post("/admin/email-suppressed/reset", { email });
      toast.success(`Delivery resumed for ${email} (${data.cleared} log entries cleared)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setResetting("");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB"); } catch { return iso; }
  };

  return (
    <div data-testid="admin-email-health">
      <PageHeader
        title="Email Health"
        icon={MailWarning}
        subtitle="Recipients auto-suppressed after 3+ delivery failures in the last 30 days. Reset to resume sending."
      />
      <div className="card-soft p-5">
        {rows === null ? (
          <div className="h-40 animate-pulse rounded-xl bg-white/70" />
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-14 text-center" data-testid="email-health-empty">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <p className="text-base font-semibold text-slate-800">All clear</p>
            <p className="max-w-md text-sm text-slate-500">No suppressed recipients right now. Every address is receiving mail normally.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead className="text-center">Failures (30d)</TableHead>
                <TableHead>Last Failed</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.email} data-testid={`suppressed-row-${r.email}`}>
                  <TableCell className="font-medium text-slate-800">{r.email}</TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-600">
                      {r.failures}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(r.last_failed)}</TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => reset(r.email)}
                      disabled={resetting === r.email}
                      data-testid={`reset-suppression-${r.email}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
                    >
                      <RotateCcw className={`h-3.5 w-3.5 ${resetting === r.email ? "animate-spin" : ""}`} />
                      {resetting === r.email ? "Resetting..." : "Reset"}
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
